import { Button, Result, Space, Typography } from 'antd';
import { ReloadOutlined, HomeOutlined, BugOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

// Parse error to determine if it's a network/server error
const getErrorInfo = (error: Error) => {
  const errorMessage = error.message.toLowerCase();

  // Check for common error patterns
  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return {
      title: 'Connection Error',
      description: 'Unable to connect to the server. Please check your internet connection.',
      status: 'error' as const,
      suggestions: [
        'Check your internet connection',
        'Verify the server is running',
        'Try refreshing the page',
      ],
    };
  }

  if (errorMessage.includes('500') || errorMessage.includes('server error')) {
    return {
      title: 'Server Error',
      description: 'The server encountered an error. Our team has been notified.',
      status: 'error' as const,
      suggestions: [
        'Try again in a few moments',
        'Return to the home page',
        'Contact support if the issue persists',
      ],
    };
  }

  if (errorMessage.includes('404') || errorMessage.includes('not found')) {
    return {
      title: 'Page Not Found',
      description: 'The page or resource you are looking for could not be found.',
      status: '404' as const,
      suggestions: [
        'Check the URL is correct',
        'Return to the home page',
        'Use the navigation menu',
      ],
    };
  }

  if (errorMessage.includes('timeout')) {
    return {
      title: 'Request Timeout',
      description: 'The request took too long to complete. The server may be busy.',
      status: 'error' as const,
      suggestions: [
        'Try again in a moment',
        'Check your internet speed',
        'Contact support if this persists',
      ],
    };
  }

  // Default error
  return {
    title: 'Something Went Wrong',
    description: error.message || 'An unexpected error occurred in the application.',
    status: 'error' as const,
    suggestions: [
      'Try refreshing the page',
      'Return to the home page',
      'Contact support if the problem continues',
    ],
  };
};

export default function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const errorInfo = getErrorInfo(error);

  return (
    <div style={{ padding: '50px 24px', maxWidth: 800, margin: '0 auto' }}>
      <Result
        status={errorInfo.status}
        title={errorInfo.title}
        subTitle={errorInfo.description}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              key="retry"
              onClick={resetErrorBoundary}
            >
              Try Again
            </Button>
            <Button
              icon={<HomeOutlined />}
              key="home"
              onClick={() => window.location.href = '/'}
            >
              Go Home
            </Button>
          </Space>
        }
      >
        {errorInfo.suggestions.length > 0 && (
          <div style={{ textAlign: 'left', marginTop: 24 }}>
            <Paragraph>
              <Text strong>What you can try:</Text>
            </Paragraph>
            <ul style={{ paddingLeft: 24 }}>
              {errorInfo.suggestions.map((suggestion, index) => (
                <li key={index}>
                  <Text>{suggestion}</Text>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Result>

      {import.meta.env.DEV && (
        <details style={{
          marginTop: 32,
          padding: 16,
          backgroundColor: '#f5f5f5',
          borderRadius: 4,
          border: '1px solid #d9d9d9'
        }}>
          <summary style={{
            cursor: 'pointer',
            fontWeight: 'bold',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <BugOutlined />
            Error Details (Development Only)
          </summary>
          <div style={{
            backgroundColor: '#fff',
            padding: 12,
            borderRadius: 4,
            marginTop: 8
          }}>
            <Paragraph>
              <Text strong>Error Type:</Text> <Text code>{error.name}</Text>
            </Paragraph>
            <Paragraph>
              <Text strong>Message:</Text> <Text>{error.message}</Text>
            </Paragraph>
            <Paragraph>
              <Text strong>Stack Trace:</Text>
            </Paragraph>
            <pre style={{
              overflow: 'auto',
              fontSize: 11,
              backgroundColor: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
              maxHeight: 300
            }}>
              {error.stack}
            </pre>
          </div>
        </details>
      )}
    </div>
  );
}
